import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import AppLoading from '../AppLoading';
import { assessmentAnswerText, assessmentQuestionEvaluation } from './assessmentAnswerUtils';
import {
  findAssessmentEvaluationForEvent,
  findEvaluationEventForStep,
  getEventScoreRevision,
  isEvaluationFailure,
  isEvaluationManualReview,
  isEvaluationPending,
  isInitialEvaluationEvent,
  limitFactorLabel,
  normalizeCandidateEvaluationEvents,
  resolveCandidateEvaluationScore,
} from './candidateEvaluationJourney';
import { orderWorkflowSubstages } from './workflowSubstageOrder';
import {
  NOTE_PRIORITIES,
  groupTeamNoteThreads,
} from './teamNoteUtils';
import './ApplicantDetailsPopupV2.css';

const STATIC_STEP_NAMES = {
  'all-applicants-1': 'All Applicants',
  'filter-applicants': 'Fit Applicants',
  'offer-sent': 'Offer Sent',
  'hired-applicants': 'Hired',
  'move-to-onboarding': 'Move to Onboarding',
  'background-checks': 'Background Checks',
  orientation: 'Orientation'
};

const asTimestamp = (value) => {
  if (!value) return 0;
  const timestamp = typeof value === 'number' ? value : new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const firstPopulatedValue = (...values) => values.find((value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return Boolean(value.trim());
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
});

const normalizeHref = (value) => {
  if (typeof value !== 'string' || !value.trim()) return '';
  const trimmed = value.trim();
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const humanize = (value) => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const displayText = (value) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(displayText).filter(Boolean).join('\n');
  if (typeof value === 'object') {
    return displayText(firstValue(
      value.overall_summary,
      value.summary,
      value.analysis,
      value.feedback,
      value.reasoning,
      value.text,
      ''
    ));
  }
  return '';
};

const normalizeList = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string' || typeof item === 'number') return String(item).trim();
        if (!item || typeof item !== 'object') return '';
        return String(firstValue(item.name, item.skill, item.label, item.title, item.value, '')).trim();
      })
      .filter(Boolean);
  }
  if (value && typeof value === 'object') {
    if (Array.isArray(value.normalized)) return normalizeList(value.normalized);
    return normalizeList(Object.values(value));
  }
  if (typeof value === 'string') {
    return value.split(/\n|,|•/).map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const normalizeCvEntries = (value, type) => {
  let rawEntries = value;
  if (rawEntries && !Array.isArray(rawEntries) && typeof rawEntries === 'object') {
    rawEntries = Object.values(rawEntries);
  }
  if (typeof rawEntries === 'string') {
    rawEntries = rawEntries.split(/\n{2,}|\n(?=[^\s•-])/).map((entry) => entry.trim()).filter(Boolean);
  }
  if (!Array.isArray(rawEntries)) return [];

  return rawEntries.map((entry, index) => {
    if (typeof entry === 'string' || typeof entry === 'number') {
      return {
        id: `${type}-${index}`,
        title: String(entry),
        subtitle: '',
        meta: '',
        description: ''
      };
    }

    const item = entry || {};
    const descriptionSource = firstPopulatedValue(
      item.description,
      item.summary,
      item.details,
      item.responsibilities,
      ''
    );
    let description = Array.isArray(descriptionSource)
      ? descriptionSource.map((line) => `- ${line}`).join('\n')
      : String(descriptionSource || '');

    const achievements = normalizeList(firstPopulatedValue(item.notableAchievements, item.achievements, item.highlights));
    if (achievements.length) {
      const achievementMarkdown = achievements.map((line) => `- ${line}`).join('\n');
      description = [description, achievementMarkdown].filter(Boolean).join('\n\n');
    }

    if (type === 'education') {
      const degree = firstValue(item.degree, item.qualification, item.program, item.title, 'Education');
      const major = firstValue(item.major, item.field_of_study, item.fieldOfStudy, '');
      const title = major && !String(degree).toLowerCase().includes(String(major).toLowerCase())
        ? `${degree} · ${major}`
        : degree;
      const subtitle = firstValue(item.school, item.institution, item.university, item.organization, '');
      const date = firstValue(
        item.date_range,
        item.dateRange,
        item.dates,
        item.year,
        item.graduationYear,
        [item.startDate, item.endDate].filter(Boolean).join(' – '),
        ''
      );
      const location = firstValue(item.location, item.city, '');
      return {
        id: `${type}-${index}`,
        title: String(title),
        subtitle: String(subtitle),
        meta: [date, location].filter(Boolean).join(' · '),
        description
      };
    }

    const title = firstValue(item.title, item.position, item.role, item.jobTitle, item.job_title, 'Experience');
    const subtitle = firstValue(item.company, item.employer, item.organization, item.companyName, '');
    const date = firstValue(
      item.date_range,
      item.dateRange,
      item.dates,
      item.duration,
      item.period,
      [item.startDate, item.currentlyActive ? 'Present' : item.endDate].filter(Boolean).join(' – '),
      ''
    );
    const location = firstValue(item.location, item.city, item.workLocation, '');
    return {
      id: `${type}-${index}`,
      title: String(title),
      subtitle: String(subtitle),
      meta: [date, location].filter(Boolean).join(' · '),
      description
    };
  });
};

const resolveCvSections = (applicantData) => {
  const profile = applicantData || {};
  const metadata = profile.metadata || {};
  const storedCv = profile.cv && typeof profile.cv === 'object' ? profile.cv : {};
  const fullCv = firstPopulatedValue(metadata.full_cv, profile.full_cv, profile.fullCv, profile.cvData, {}) || {};
  const parsed = firstPopulatedValue(profile.parsedData, storedCv.parsedData, metadata.parsedData, {}) || {};
  const extraction = firstPopulatedValue(profile.extraction, metadata.extraction, {}) || {};

  const workSource = firstPopulatedValue(
    fullCv.work_experience,
    fullCv.workExperience,
    fullCv.experience,
    storedCv.work_experience,
    storedCv.workExperience,
    storedCv.experience,
    parsed.work_experience,
    parsed.workExperience,
    parsed.experience,
    extraction.experience?.timeline,
    profile.work_experience,
    profile.workExperience,
    profile.experience,
    metadata.experience,
    []
  );
  const educationSource = firstPopulatedValue(
    fullCv.education,
    storedCv.education,
    parsed.education,
    extraction.education,
    profile.education,
    metadata.education,
    []
  );
  const skillSource = firstPopulatedValue(
    fullCv.skills,
    storedCv.skills,
    parsed.skills,
    extraction.skills?.normalized,
    extraction.skills?.tools,
    extraction.skills,
    profile.skills,
    metadata.tags,
    []
  );

  return {
    work: normalizeCvEntries(workSource, 'work'),
    education: normalizeCvEntries(educationSource, 'education'),
    skills: Array.from(new Set(normalizeList(skillSource)))
  };
};

const flattenWorkflow = (workflowStages, dynamicSubstages, connection, applicantData, department) => {
  const currentSubstageId = firstValue(connection?.substageId, applicantData?.substageId, 'all-applicants-1');
  const currentStageId = firstValue(connection?.stageId, applicantData?.stageId, 'stage-1');
  const staticStepsByStage = {
    'stage-1': ['offer-sent', 'hired-applicants', 'move-to-onboarding'],
    'stage-2': ['background-checks'],
    'stage-3': ['orientation']
  };
  const configuredStageIds = new Set([
    'stage-1',
    ...Object.keys(workflowStages || {}),
    ...Object.keys(dynamicSubstages || {}),
    currentStageId
  ]);
  const stageIds = [...configuredStageIds]
    .map((stageId, insertionIndex) => ({ stageId, insertionIndex, stage: workflowStages?.[stageId] || {} }))
    .sort((left, right) => {
      const leftOrder = Number(firstValue(left.stage.order, left.stage.orderIndex, left.insertionIndex));
      const rightOrder = Number(firstValue(right.stage.order, right.stage.orderIndex, right.insertionIndex));
      return leftOrder - rightOrder;
    });
  const allConfiguredSubstages = stageIds.flatMap(({ stageId, stage }) => {
    if (dynamicSubstages?.[stageId]) return dynamicSubstages[stageId];
    return Object.entries(stage?.substages || {}).map(([id, substage]) => ({ id, ...(substage || {}) }));
  });
  const currentExistsInWorkflow = allConfiguredSubstages.some((substage) => substage.id === currentSubstageId)
    || Object.values(staticStepsByStage).flat().includes(currentSubstageId);
  const initialStageName = firstValue(workflowStages?.['stage-1']?.name, 'Hiring Workflow');
  const steps = [{
    id: 'initial-analysis',
    stageId: 'stage-1',
    stageName: initialStageName,
    title: 'Initial Analysis',
    type: 'initial-analysis'
  }];

  const appendStep = (substage, stageId, stageName) => {
    steps.push({
      // Preserve scheduler configuration such as duration and interviewers.
      ...substage,
      id: substage.id,
      stageId,
      stageName,
      title: firstValue(STATIC_STEP_NAMES[substage.id], substage.name, substage.title, humanize(substage.id)),
      type: substage.type || 'static',
      assessmentId: substage.link || '',
      description: firstValue(substage.description, substage.subtitle, '')
    });
  };

  stageIds.forEach(({ stageId, stage }, stageIndex) => {
    const stageName = firstValue(stage?.name, `Stage ${stageIndex + 1}`);
    const configuredSubstages = dynamicSubstages?.[stageId]
      || Object.entries(stage?.substages || {}).map(([id, substage]) => ({ id, ...(substage || {}) }));
    const dynamicForBoard = configuredSubstages.filter((substage) => {
      if (substage.id === 'filter-applicants' || substage.id === 'all-applicants-1') return false;
      return !(staticStepsByStage[stageId] || []).includes(substage.id);
    });
    const staticIds = staticStepsByStage[stageId] || [];

    // Both the kanban board and this timeline consume this exact shared order.
    if (stageId !== 'stage-1') {
      staticIds.forEach((id) => appendStep({ id, type: 'static' }, stageId, stageName));
    }
    orderWorkflowSubstages(dynamicForBoard, department)
      .forEach((substage) => appendStep(substage, stageId, stageName));
    if (stageId === 'stage-1') {
      staticIds.forEach((id) => appendStep({ id, type: 'static' }, stageId, stageName));
    }
  });

  const currentIsInitialAnalysis = currentSubstageId === 'all-applicants-1' || currentSubstageId === 'filter-applicants';

  // A step that exists but was filtered out belongs to another department and
  // must not leak back into this job's timeline as a synthetic current step.
  if (!currentIsInitialAnalysis && !currentExistsInWorkflow && !steps.some((step) => step.id === currentSubstageId && step.stageId === currentStageId)) {
    steps.push({
      id: currentSubstageId,
      stageId: currentStageId,
      stageName: humanize(currentStageId),
      title: firstValue(STATIC_STEP_NAMES[currentSubstageId], humanize(currentSubstageId), 'Current Step'),
      type: 'static'
    });
  }

  const matchingCurrentIndex = steps.findIndex((step) => step.id === currentSubstageId && step.stageId === currentStageId);
  const currentIndex = currentIsInitialAnalysis || matchingCurrentIndex < 0 ? 0 : matchingCurrentIndex;
  return { steps, currentIndex };
};

const ASSESSMENT_STEP_TYPES = new Set(['assessment', 'ai-assessment', 'ai-interview']);
const isAssessmentStep = (step) => ASSESSMENT_STEP_TYPES.has(String(step?.type || '').toLowerCase());
const isInterviewStep = (step) => String(step?.type || '').toLowerCase() === 'interview';

const findAssessmentForStep = (assessmentData, step) => {
  if (!step) return null;
  if (assessmentData?.[step.id]) return assessmentData[step.id];
  return Object.values(assessmentData || {}).find((entry) => {
    const linkedIds = [entry?.assessmentId, entry?.assessment?.assessmentId, entry?.assessment?.id]
      .filter(Boolean)
      .map(String);
    return step.assessmentId && linkedIds.includes(String(step.assessmentId));
  }) || null;
};

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const decorateMentions = (children, mentions = []) => {
  const labels = mentions
    .map((mention) => firstValue(mention?.name, mention?.email?.split('@')[0], ''))
    .filter(Boolean)
    .sort((left, right) => right.length - left.length);
  if (!labels.length) return children;
  const pattern = new RegExp(`(@(?:${labels.map(escapeRegExp).join('|')}))`, 'gi');
  const normalizedLabels = new Set(labels.map((label) => label.toLowerCase()));

  return React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      return child.split(pattern).map((part, index) => (
        part.startsWith('@') && normalizedLabels.has(part.slice(1).toLowerCase())
          ? <span className="adp2-team-mention" key={`${part}-${index}`}>{part}</span>
          : part
      ));
    }
    if (React.isValidElement(child) && child.props?.children) {
      return React.cloneElement(child, {}, decorateMentions(child.props.children, mentions));
    }
    return child;
  });
};

const MarkdownBody = ({ children, mentions = [] }) => {
  if (!children) return null;
  return (
    <div className="adp2-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children: paragraphChildren }) => <p>{decorateMentions(paragraphChildren, mentions)}</p>,
          li: ({ children: listChildren }) => <li>{decorateMentions(listChildren, mentions)}</li>,
        }}
      >
        {String(children)}
      </ReactMarkdown>
    </div>
  );
};

const EmptyBlock = ({ children }) => <p className="adp2-empty">{children}</p>;

const ApplicantDetailsPopupV2 = ({
  applicantData,
  isLoading,
  applicantConnectionData,
  applicantIndexData,
  candidateEvaluation,
  loadingCandidateEvaluation,
  applicantEmail,
  contactInfo,
  candidateAnalysis,
  applicationArtifacts,
  workflowStages,
  dynamicSubstages,
  selectedJobDepartment,
  interviews,
  loadingInterviews,
  assessmentData,
  loadingAssessmentData,
  teamNotes,
  teamDirectory,
  messages,
  userUID,
  newNoteContent,
  setNewNoteContent,
  newMessage,
  setNewMessage,
  teamMessageBoxRef,
  messageBoxRef,
  messagesRef,
  onMessageKeyDown,
  onAddNote,
  onSendMessage,
  onToggleStarred,
  isStarred,
  togglingStarred,
  onCopyEmail,
  onClose,
  onOpenResume,
  resumeLink,
  onAnalyze,
  isAnalyzing,
  onReject,
  onRemove,
  onScheduleInterview,
  onRescheduleInterview,
  onCancelInterview,
  offerApproval,
  onViewApprovalDetails,
  buddyInfo,
  firstWeekChecklist,
  checklistProgress,
  onToggleChecklist,
  fitScore,
  initialTab = 'cv',
  isRTL
}) => {
  useUiLanguage();
  const { t } = useTranslation('applicants');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activeAnalysisTab, setActiveAnalysisTab] = useState('summary');
  const [actionsOpen, setActionsOpen] = useState(false);
  const [notePriority, setNotePriority] = useState('normal');
  const [noteMentions, setNoteMentions] = useState([]);
  const [replyMentions, setReplyMentions] = useState([]);
  const [mentionContext, setMentionContext] = useState(null);
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [replyingToNoteId, setReplyingToNoteId] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const actionsRef = useRef(null);
  const resolvedDepartment = firstValue(
    selectedJobDepartment,
    applicantConnectionData?.department,
    applicantData?.department,
    ''
  );
  const { steps, currentIndex } = useMemo(
    () => flattenWorkflow(workflowStages, dynamicSubstages, applicantConnectionData, applicantData, resolvedDepartment),
    [workflowStages, dynamicSubstages, applicantConnectionData, applicantData, resolvedDepartment]
  );
  const [selectedJourneyIndex, setSelectedJourneyIndex] = useState(currentIndex);
  const cvSections = useMemo(() => resolveCvSections(applicantData), [applicantData]);

  useEffect(() => {
    setSelectedJourneyIndex(currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    const closeActions = (event) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target)) setActionsOpen(false);
    };
    document.addEventListener('mousedown', closeActions);
    return () => document.removeEventListener('mousedown', closeActions);
  }, []);

  useEffect(() => {
    if (activeTab === 'messages' && messagesRef?.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [activeTab, messages, messagesRef]);

  useEffect(() => {
    if (activeTab !== 'messages' || !messageBoxRef?.current) return;
    const textarea = messageBoxRef.current;
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 22;
    const verticalPadding = (parseFloat(computedStyle.paddingTop) || 0)
      + (parseFloat(computedStyle.paddingBottom) || 0);
    const maximumHeight = (lineHeight * 8) + verticalPadding;

    // Start at one line, grow with wraps/newlines, then keep further content
    // inside the composer with its own scrollbar after eight visible lines.
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, maximumHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maximumHeight ? 'auto' : 'hidden';
  }, [activeTab, newMessage, messageBoxRef]);

  const candidateName = firstValue(applicantData?.username, applicantData?.name, 'Applicant');
  const firstName = String(candidateName).trim().split(/\s+/)[0];
  const linkedin = normalizeHref(firstValue(applicantData?.linkedin, applicantData?.linkedIn, contactInfo?.linkedin, ''));
  const phone = firstValue(contactInfo?.phone, applicantData?.phone, applicantData?.phoneNumber, '');
  const evaluationLedger = candidateEvaluation || applicantConnectionData?.candidateEvaluation || null;
  const evaluationEvents = useMemo(
    () => normalizeCandidateEvaluationEvents(evaluationLedger),
    [evaluationLedger]
  );
  const currentEvaluationEvent = evaluationEvents.find(
    (event) => event.eventId === String(evaluationLedger?.current?.eventId || '')
  ) || null;
  const resolvedFitScore = resolveCandidateEvaluationScore(
    evaluationLedger,
    fitScore,
    applicantIndexData?.fit_score,
    applicantIndexData?.fitScore,
    applicantConnectionData?.fit_score,
    applicantConnectionData?.fitScore
  );
  const currentScoreRevision = getEventScoreRevision(currentEvaluationEvent);
  const legacyScoreDeltaValue = firstValue(
    applicantIndexData?.fit_score_delta,
    applicantIndexData?.fitScoreDelta,
    applicantConnectionData?.fitScoreDelta,
    applicantConnectionData?.scoreDelta
  );
  const legacyScoreDelta = Number.isFinite(Number(legacyScoreDeltaValue)) ? Number(legacyScoreDeltaValue) : null;
  const scoreDelta = currentScoreRevision.delta ?? legacyScoreDelta;
  const selectedStep = steps[selectedJourneyIndex] || steps[currentIndex];
  const normalizedAnalysis = candidateAnalysis && typeof candidateAnalysis === 'object' ? candidateAnalysis : null;
  const noteThreads = useMemo(() => groupTeamNoteThreads(teamNotes), [teamNotes]);
  const teamMembers = useMemo(
    () => Object.values(teamDirectory?.byUid || {}).filter((member, index, members) => (
      member?.uid && members.findIndex((candidate) => candidate.uid === member.uid) === index
    )),
    [teamDirectory]
  );
  const visibleJourneySteps = steps.slice(0, Math.max(currentIndex + 1, 1));
  const visibleInterviewSteps = visibleJourneySteps.filter(isInterviewStep);

  const interviewsForStep = (step) => {
    if (!isInterviewStep(step)) return [];
    const explicitMatches = (interviews || []).filter((interview) => {
      const linkedStepId = firstValue(
        interview.workflowSubstageId,
        interview.substageId,
        interview.workflowStepId,
        interview.interviewStepId
      );
      if (!linkedStepId || String(linkedStepId) !== String(step.id)) return false;
      return !interview.workflowStageId || String(interview.workflowStageId) === String(step.stageId);
    });

    // Older interview records predate workflow-step IDs. Show those records once:
    // on the current interview step, or the latest reached interview step after
    // the candidate has moved on. This avoids duplicating them across rounds.
    const fallbackStep = isInterviewStep(steps[currentIndex])
      ? steps[currentIndex]
      : (visibleInterviewSteps[visibleInterviewSteps.length - 1] || null);
    if (!fallbackStep || fallbackStep.id !== step.id || fallbackStep.stageId !== step.stageId) return explicitMatches;
    const legacyInterviews = (interviews || []).filter((interview) => !firstValue(
      interview.workflowSubstageId,
      interview.substageId,
      interview.workflowStepId,
      interview.interviewStepId
    ));
    return [...explicitMatches, ...legacyInterviews];
  };

  const evaluationContextForStep = (step) => {
    const relatedAssessment = isAssessmentStep(step)
      ? findAssessmentForStep(assessmentData, step)
      : null;
    const relatedInterviews = isInterviewStep(step)
      ? interviewsForStep(step)
      : [];
    const event = findEvaluationEventForStep(
      evaluationLedger,
      step,
      { relatedAssessment, relatedInterviews }
    );
    return { event, relatedAssessment, relatedInterviews };
  };

  /** Show useful step-specific context instead of repeating the parent stage. */
  const timelineStepSubtitle = (step, index) => {
    if (step?.id === 'initial-analysis') {
      return firstValue(
        applicantConnectionData?.jobAppliedFor,
        applicantConnectionData?.jobTitle,
        applicantConnectionData?.roleTitle,
        applicantData?.jobTitle,
        applicantData?.jobAppliedFor,
        applicantData?.role,
        ''
      );
    }

    const { event, relatedAssessment, relatedInterviews } = evaluationContextForStep(step);
    if (event) {
      const eventStatus = String(event.status || '').toLowerCase().replace(/-/g, '_');
      if (isEvaluationPending(event)) {
        return t('details.journeyEvaluation.timelineEvaluating', 'Evaluation in progress');
      }
      if (isEvaluationManualReview(event)) {
        return t('details.journeyEvaluation.manualReview', 'Human Review Required');
      }
      if (isEvaluationFailure(event)) {
        return t('details.journeyEvaluation.technicalFailure', 'Evaluation Could Not Be Completed');
      }
      if (eventStatus === 'void') {
        return t('details.journeyEvaluation.timelineRequirementsNotMet', 'Requirements not met');
      }

      const revision = getEventScoreRevision(event);
      if (revision.direction === 'increased') {
        return t('details.journeyEvaluation.scoreIncreased', 'Fit Score Increased');
      }
      if (revision.direction === 'decreased') {
        return t('details.journeyEvaluation.scoreDecreased', 'Fit Score Decreased');
      }
      if (revision.direction === 'unchanged') {
        return t('details.journeyEvaluation.scoreUnchanged', 'Fit Score Unchanged');
      }
      return t('details.journeyEvaluation.timelineComplete', 'Evaluation completed');
    }

    const assessmentStatus = String(firstValue(
      relatedAssessment?.liveReview?.evaluationState?.status,
      relatedAssessment?.evaluationState?.status,
      relatedAssessment?.response?.status,
      ''
    )).toLowerCase().replace(/-/g, '_');
    if (['queued', 'pending', 'evaluating', 'processing', 'in_progress', 'submitted'].includes(assessmentStatus)) {
      return t('details.journeyEvaluation.timelineEvaluating', 'Evaluation in progress');
    }
    if (assessmentStatus === 'manual_review') {
      return t('details.journeyEvaluation.manualReview', 'Human Review Required');
    }
    if (['technical_failure', 'evaluation_failed', 'failed'].includes(assessmentStatus)) {
      return t('details.journeyEvaluation.technicalFailure', 'Evaluation Could Not Be Completed');
    }
    if (['completed', 'evaluated', 'scored'].includes(assessmentStatus)) {
      return t('details.journeyEvaluation.timelineComplete', 'Evaluation completed');
    }

    const interviewStatus = String(relatedInterviews?.[0]?.status || '').toLowerCase().replace(/-/g, '_');
    if (['scheduled', 'rescheduled', 'confirmed'].includes(interviewStatus)) {
      return t('details.journeyEvaluation.timelineInterviewScheduled', 'Interview scheduled');
    }
    if (interviewStatus === 'cancelled') {
      return t('details.journeyEvaluation.timelineInterviewCancelled', 'Interview cancelled');
    }
    if (interviewStatus === 'completed') {
      return t('details.journeyEvaluation.timelineComplete', 'Evaluation completed');
    }

    if (index < currentIndex) {
      return t('details.journeyEvaluation.timelineStepComplete', 'Completed');
    }
    if (index === currentIndex) {
      return t('details.journeyEvaluation.timelineCurrent', 'Current step');
    }
    return '';
  };

  const formatDateTime = (value) => {
    const timestamp = asTimestamp(value);
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleString(isRTL ? 'ar-EG' : undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatMessageTimestamp = (value) => {
    const timestamp = asTimestamp(value);
    if (!timestamp) return '';
    const messageDate = new Date(timestamp);
    const today = new Date();
    const isToday = messageDate.toDateString() === today.toDateString();
    return messageDate.toLocaleString(isRTL ? 'ar-EG' : undefined, isToday
      ? { hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const messageDateKey = (value) => {
    const timestamp = asTimestamp(value);
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  };

  const formatMessageDateLabel = (value) => {
    const timestamp = asTimestamp(value);
    if (!timestamp) return '';
    const messageDate = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (messageDate.toDateString() === today.toDateString()) return uiText('Today');
    if (messageDate.toDateString() === yesterday.toDateString()) return uiText('Yesterday');
    return messageDate.toLocaleDateString(isRTL ? 'ar-EG' : undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...(messageDate.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {})
    });
  };

  const getMember = (uid, email) => {
    return (uid && teamDirectory?.byUid?.[uid]) || (email && teamDirectory?.byEmail?.[email]) || {};
  };

  const tabs = [
    { id: 'cv', label: 'CV Analysis' },
    { id: 'journey', label: 'Candidate Journey' },
    { id: 'team', label: 'Team Notes' },
    { id: 'messages', label: 'Messages' }
  ];

  const invokeAction = (callback) => {
    setActionsOpen(false);
    if (callback) callback();
  };

  const renderCv = () => (
    <section className="adp2-tab-panel adp2-cv" aria-labelledby="adp2-tab-cv">
      <div className="adp2-section-heading-row">
        <h2>{uiText("Work Experience")}</h2>
      </div>
      {cvSections.work.length ? (
        <div className="adp2-cv-list">
          {cvSections.work.map((item) => (
            <article className="adp2-cv-entry" key={item.id}>
              <h3>{item.title}</h3>
              {item.subtitle && <p className="adp2-cv-subtitle">{uiText(item.subtitle)}</p>}
              {item.meta && <p className="adp2-muted">{item.meta}</p>}
              <MarkdownBody>{item.description}</MarkdownBody>
            </article>
          ))}
        </div>
      ) : <EmptyBlock>{uiText("No work experience was included in this profile.")}</EmptyBlock>}

      <h2>{uiText("Education")}</h2>
      {cvSections.education.length ? (
        <div className="adp2-cv-list">
          {cvSections.education.map((item) => (
            <article className="adp2-cv-entry" key={item.id}>
              <h3>{item.title}</h3>
              {item.subtitle && <p className="adp2-cv-subtitle">{uiText(item.subtitle)}</p>}
              {item.meta && <p className="adp2-muted">{item.meta}</p>}
              <MarkdownBody>{item.description}</MarkdownBody>
            </article>
          ))}
        </div>
      ) : <EmptyBlock>{uiText("No education history was included in this profile.")}</EmptyBlock>}

      <h2>{uiText("Skills")}</h2>
      {cvSections.skills.length ? (
        <div className="adp2-skill-list">
          {cvSections.skills.map((skill) => <span key={skill}>{skill}</span>)}
        </div>
      ) : <EmptyBlock>{uiText("No skills were included in this profile.")}</EmptyBlock>}

      {Boolean(applicationArtifacts?.coverLetterUrl || applicationArtifacts?.portfolioLinks?.length || applicationArtifacts?.responseEntries?.length) && (
        <div className="adp2-materials">
          <h2>{uiText("Application Materials")}</h2>
          <div className="adp2-material-links">
            {applicationArtifacts.coverLetterUrl && (
              <a href={applicationArtifacts.coverLetterUrl} target="_blank" rel="noreferrer">
                <i className="fa-solid fa-file-lines" />{uiText(" Cover letter")}</a>
            )}
            {(applicationArtifacts.portfolioLinks || []).map((link) => (
              <a href={link} target="_blank" rel="noreferrer" key={link}>
                <i className="fa-solid fa-arrow-up-right-from-square" />{uiText(" Portfolio")}</a>
            ))}
          </div>
          {(applicationArtifacts.responseEntries || []).map((entry) => (
            <article className="adp2-response" key={entry.label}>
              <h3>{uiText(entry.label)}</h3>
              <MarkdownBody>{entry.answer}</MarkdownBody>
            </article>
          ))}
        </div>
      )}
    </section>
  );

  const renderAnalysisList = (items, emptyText) => {
    if (!items?.length) return <EmptyBlock>{emptyText}</EmptyBlock>;
    return (
      <ul className="adp2-analysis-list">
        {items.map((item, index) => {
          const text = displayText(item);
          return text ? <li key={`${text}-${index}`}>{text}</li> : null;
        })}
      </ul>
    );
  };

  const evaluationSummaryTitle = (event) => {
    const sourceType = String(event?.sourceType || '').toLowerCase();
    if (isInitialEvaluationEvent(event)) {
      return t('details.journeyEvaluation.initialSummary', 'Initial Analysis Summary');
    }
    if (sourceType.includes('interview')) {
      return t('details.journeyEvaluation.interviewSummary', 'Interview Summary');
    }
    if (sourceType.includes('assessment')) {
      return t('details.journeyEvaluation.assessmentSummary', 'Assessment Summary');
    }
    return t('details.journeyEvaluation.evaluationSummary', 'Evaluation Summary');
  };

  const renderEvaluationJourneyEvent = (event, { allowLegacyScore = false } = {}) => {
    if (loadingCandidateEvaluation) {
      return (
        <div className="adp2-evaluation-loading">
          <AppLoading inline label={t('details.journeyEvaluation.loading', 'Loading evaluation history…')} />
        </div>
      );
    }

    if (!event) {
      // Older records have only one scalar score. Show it on Initial Analysis
      // without fabricating a stage-specific score revision or rationale.
      if (allowLegacyScore && resolvedFitScore !== null) {
        return (
          <div className="adp2-fit-card adp2-fit-card--legacy">
            <div>
              <p className="adp2-subheading">{t('details.journeyEvaluation.legacyScore', 'Saved Fit Score')}</p>
              <p className="adp2-muted">{t('details.journeyEvaluation.legacyScoreHelp', 'Historical score; no score-change explanation was recorded.')}</p>
            </div>
            <strong>{resolvedFitScore}/100</strong>
          </div>
        );
      }
      return null;
    }

    const scoreRevisionPayload = event.scoreRevision || event.score_revision || {};
    const eventRationale = event.rationale || scoreRevisionPayload.rationale || event.reason || '';

    if (isEvaluationPending(event)) {
      return (
        <section className="adp2-evaluation-state is-pending" aria-live="polite">
          <AppLoading inline label={t('details.journeyEvaluation.evaluating', 'Preparing this evaluation…')} />
          <p>{t('details.journeyEvaluation.evaluatingHelp', 'The evidence is saved. The score will update here when the evaluation is complete.')}</p>
        </section>
      );
    }

    if (isEvaluationManualReview(event)) {
      return (
        <section className="adp2-evaluation-state is-review" role="status">
          <i className="fa-regular fa-eye" aria-hidden="true" />
          <div>
            <h3>{t('details.journeyEvaluation.manualReview', 'Human Review Required')}</h3>
            <MarkdownBody>{eventRationale || event.summary || t('details.journeyEvaluation.manualReviewHelp', 'The available evidence was not sufficient for an automatic score change.')}</MarkdownBody>
            <p className="adp2-muted">{t('details.journeyEvaluation.scoreNotChanged', 'The candidate fit score was not changed.')}</p>
          </div>
        </section>
      );
    }

    if (isEvaluationFailure(event)) {
      return (
        <section className="adp2-evaluation-state is-failure" role="alert">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
          <div>
            <h3>{t('details.journeyEvaluation.technicalFailure', 'Evaluation Could Not Be Completed')}</h3>
            <MarkdownBody>{eventRationale || event.error || t('details.journeyEvaluation.technicalFailureHelp', 'A technical problem interrupted this evaluation.')}</MarkdownBody>
            <p className="adp2-muted">{t('details.journeyEvaluation.scoreNotChanged', 'The candidate fit score was not changed.')}</p>
          </div>
        </section>
      );
    }

    const revision = getEventScoreRevision(event);
    const isInitial = isInitialEvaluationEvent(event) || revision.direction === 'initial';
    const afterScore = revision.after ?? (isInitial && Number.isFinite(Number(event.performanceScore))
      ? Math.round(Number(event.performanceScore))
      : null);
    const direction = revision.direction || (
      revision.delta > 0 ? 'increased' : revision.delta < 0 ? 'decreased' : 'unchanged'
    );
    const scoreTitle = isInitial
      ? t('details.journeyEvaluation.initialScore', 'Initial Fit Score')
      : direction === 'increased'
        ? t('details.journeyEvaluation.scoreIncreased', 'Fit Score Increased')
        : direction === 'decreased'
          ? t('details.journeyEvaluation.scoreDecreased', 'Fit Score Decreased')
          : t('details.journeyEvaluation.scoreUnchanged', 'Fit Score Unchanged');
    const whyTitle = direction === 'decreased'
      ? t('details.journeyEvaluation.whyDecreased', 'Why The Score Decreased')
      : direction === 'unchanged'
        ? t('details.journeyEvaluation.whyUnchanged', 'Why The Score Stayed The Same')
        : isInitial
          ? t('details.journeyEvaluation.initialFactors', 'Why This Is The Initial Score')
          : t('details.journeyEvaluation.whyIncreased', 'Why The Score Increased');
    const factorSource = event.factors || scoreRevisionPayload.factors || [];
    const rawFactors = Array.isArray(factorSource)
      ? factorSource
      : Object.values(factorSource || {});
    const factors = rawFactors
      .map((factor) => typeof factor === 'string' ? { label: factor, detail: '' } : factor)
      .filter((factor) => factor?.label)
      .slice(0, 3);

    return (
      <section className={`adp2-evaluation-event is-${direction || 'scored'}`}>
        <div className="adp2-score-revision">
          <p className="adp2-subheading">{scoreTitle}</p>
          {afterScore !== null && (
            <p className="adp2-score-revision-value">
              <strong>{afterScore}</strong><span>/100</span>
              {!isInitial && revision.delta !== null && (
                <em className={revision.delta < 0 ? 'is-negative' : revision.delta > 0 ? 'is-positive' : ''}>
                  ({revision.delta > 0 ? '+' : ''}{revision.delta})
                </em>
              )}
            </p>
          )}
          {eventRationale && <MarkdownBody>{eventRationale}</MarkdownBody>}
        </div>

        {event.summary && (
          <div className="adp2-event-summary">
            <h3>{evaluationSummaryTitle(event)}</h3>
            <MarkdownBody>{event.summary}</MarkdownBody>
          </div>
        )}

        {factors.length > 0 && (
          <div className="adp2-event-factors">
            <h3>{whyTitle}</h3>
            <div className="adp2-factor-grid">
              {factors.map((factor, index) => (
                <article key={`${factor.label}-${index}`}>
                  <h4>{limitFactorLabel(factor.label)}</h4>
                  {factor.detail && (
                    <details>
                      <summary>{t('details.journeyEvaluation.readMore', 'Read more')}</summary>
                      <MarkdownBody>{factor.detail}</MarkdownBody>
                    </details>
                  )}
                </article>
              ))}
            </div>
          </div>
        )}
      </section>
    );
  };

  const renderInitialAnalysis = (initialEvent) => {
    // During migration the richer CV analysis may still live outside the
    // ledger. A ledger summary fills the gap without inventing another score.
    const analysis = normalizedAnalysis || (initialEvent ? {
      summary: initialEvent.summary || initialEvent.rationale || initialEvent.scoreRevision?.rationale || '',
      strong_points: initialEvent.strengths || [],
      weak_points: initialEvent.improvementAreas || initialEvent.concerns || [],
      interview_questions: [],
      benchmarks: {},
    } : null);
    const benchmarks = analysis?.benchmarks || {};
    const benchmarkItems = [
      ['Market Position', benchmarks.market_position],
      ['Role Level Alignment', benchmarks.role_level_alignment],
      ['Experience Benchmark', benchmarks.experience_benchmark],
      ['Evidence Quality', benchmarks.evidence_quality]
    ].filter(([, value]) => value);

    return (
      <div className="adp2-initial-analysis">
        <div className="adp2-section-heading-row">
          <h3>{uiText("Candidate Analysis")}</h3>
          <button type="button" className="adp2-text-action" onClick={onAnalyze} disabled={isAnalyzing}>
            <i className={`fa-solid ${isAnalyzing ? 'fa-spinner fa-spin' : 'fa-rotate'}`} />
            {isAnalyzing ? uiText("Analyzing…") : (analysis ? uiText("Refresh analysis") : uiText("Analyze CV"))}
          </button>
        </div>

        {!analysis ? (
          <EmptyBlock>{uiText("No candidate analysis is available yet.")}</EmptyBlock>
        ) : analysis.legacyText ? (
          <div className="adp2-analysis-card adp2-analysis-legacy">
            <MarkdownBody>{analysis.legacyText}</MarkdownBody>
          </div>
        ) : (
          <>
            <div className="adp2-analysis-tabs" role="tablist" aria-label={uiText("Candidate analysis views")}>
              {[
                { id: 'summary', label: 'Summary' },
                { id: 'interview', label: 'Interview Questions' },
                { id: 'benchmarks', label: 'Benchmarks' }
              ].map((tab) => (
                <button
                  type="button"
                  role="tab"
                  key={tab.id}
                  aria-selected={activeAnalysisTab === tab.id}
                  className={activeAnalysisTab === tab.id ? 'is-active' : ''}
                  onClick={() => setActiveAnalysisTab(tab.id)}
                >
                  {uiText(tab.label)}
                </button>
              ))}
            </div>

            {activeAnalysisTab === 'summary' && (
              <div className="adp2-analysis-grid">
                <article className="adp2-analysis-card">
                  <h4><i className="fa-solid fa-align-left" />{uiText(" Summary")}</h4>
                  <MarkdownBody>{analysis.summary || uiText("No analysis summary is available.")}</MarkdownBody>
                </article>
                <article className="adp2-analysis-card is-positive">
                  <h4><i className="fa-solid fa-circle-check" />{uiText(" Strong Points")}</h4>
                  {renderAnalysisList(analysis.strong_points, 'No strong points were recorded.')}
                </article>
                <article className="adp2-analysis-card is-warning">
                  <h4><i className="fa-solid fa-triangle-exclamation" />{uiText(" Weak Points")}</h4>
                  {renderAnalysisList(analysis.weak_points, 'No concerns were recorded.')}
                </article>
              </div>
            )}

            {activeAnalysisTab === 'interview' && (
              <article className="adp2-analysis-card">
                <h4><i className="fa-solid fa-comments" />{uiText(" Interview Questions")}</h4>
                {renderAnalysisList(analysis.interview_questions, 'No interview questions were generated.')}
              </article>
            )}

            {activeAnalysisTab === 'benchmarks' && (
              <div className="adp2-analysis-grid">
                {benchmarkItems.length ? benchmarkItems.map(([label, value]) => (
                  <article className="adp2-analysis-card" key={label}>
                    <h4><i className="fa-solid fa-chart-line" /> {uiText(label)}</h4>
                    <MarkdownBody>{value}</MarkdownBody>
                  </article>
                )) : <EmptyBlock>{uiText("No benchmark analysis is available.")}</EmptyBlock>}
                <article className="adp2-analysis-card">
                  <h4><i className="fa-solid fa-list-check" />{uiText(" Benchmark Notes")}</h4>
                  {renderAnalysisList(benchmarks.benchmark_notes, 'No benchmark notes were recorded.')}
                </article>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderAssessmentAnalysis = (step, evaluationEvent) => {
    const relatedAssessment = findAssessmentForStep(assessmentData, step);
    if (loadingAssessmentData) {
      return (
        <div className="adp2-evaluation-loading">
          <AppLoading inline label={t('details.journeyEvaluation.loadingDetails', 'Loading detailed results…')} />
        </div>
      );
    }
    if (!relatedAssessment?.response) return <EmptyBlock>{uiText("The candidate has not completed this assessment yet.")}</EmptyBlock>;

    // A queued ledger event is authoritative even if the response has already
    // arrived. Do not render the unanswered detailed report as a completed one.
    // Reused assessments can have several reports. Once a journey event exists,
    // only its immutable history record (or an explicitly matching legacy
    // pointer) is allowed; the generic latest report belongs to another step.
    const evaluation = findAssessmentEvaluationForEvent(relatedAssessment, evaluationEvent) || {};

    if (isEvaluationPending(evaluationEvent) && !evaluation?.result) return null;

    const { assessment = {}, response = {}, liveReview = {} } = relatedAssessment;
    const result = evaluation.result || {};
    const overallSummary = displayText(result.overall_summary);
    const rubricOutcome = result.rubric_outcome;
    const questions = Array.isArray(relatedAssessment.questions)
      ? relatedAssessment.questions
      : Object.values(relatedAssessment.questions || {});
    const languageEvidence = (evaluation.spokenLanguageEvidence || [])
      .map((item) => item?.evidence)
      .filter(Boolean);
    const languages = [...new Set(languageEvidence.flatMap((evidence) => evidence.detected_languages || []))];
    const hasCodeSwitching = languageEvidence.some((evidence) => evidence.code_switching_detected);
    const rawPerformanceScore = evaluationEvent?.performanceScore;
    // A manual/failed source may intentionally have no instrument score;
    // Number(null) must not turn that absence into a visible 0/100.
    const performanceScore = rawPerformanceScore === null
      || rawPerformanceScore === undefined
      || rawPerformanceScore === ''
      ? null
      : Number(rawPerformanceScore);
    const liveReviewMatchesEvent = !evaluationEvent?.sessionId
      || String(evaluationEvent.sessionId) === String(liveReview.sessionId || '');
    const selectedTranscript = liveReviewMatchesEvent && Array.isArray(liveReview.transcript)
      ? liveReview.transcript
      : [];

    return (
      <div className="adp2-assessment-analysis">
        <article className="adp2-assessment-summary">
          <div>
            <strong>{firstValue(assessment.title, step.title)}</strong>
            <p>
              {response.submittedAt ? uiText("Submitted {{date}}", { date: formatDateTime(response.submittedAt) }) : humanize(response.status || uiText("Completed"))}
            </p>
          </div>
          {rubricOutcome?.status === 'void' ? (
            <span className="is-danger" title={rubricOutcome.reason || ''}>{uiText("Void")}</span>
          ) : rubricOutcome?.status === 'manual_review' ? (
            <span className="is-warning" title={rubricOutcome.reason || ''}>{uiText("Manual review")}</span>
          ) : Number.isFinite(performanceScore) ? (
            <span title={t('details.journeyEvaluation.performanceScore', 'Performance on this evaluation')}>
              {Math.round(performanceScore)}/100
            </span>
          ) : result.fit_score !== undefined ? <span>{result.fit_score}/10</span> : null}
        </article>

        {(overallSummary || languages.length > 0) && (
          <article className="adp2-analysis-card">
            <h4><i className="fa-solid fa-chart-column" />{uiText(" Assessment Analysis")}</h4>
            {overallSummary && <MarkdownBody>{overallSummary}</MarkdownBody>}
            {languages.length > 0 && (
              <p className="adp2-muted">{uiText("Spoken: ")}{languages.map((code) => String(code).toUpperCase()).join(', ')}
                {hasCodeSwitching ? uiText(" · Code-switching detected") : ''}
              </p>
            )}
            {(rubricOutcome?.status === 'void' || rubricOutcome?.status === 'manual_review') && rubricOutcome.reason && (
              <p className="adp2-muted">{rubricOutcome.reason}</p>
            )}
          </article>
        )}

        {(result.strengths?.length > 0 || result.improvement_areas?.length > 0) && (
          <div className="adp2-assessment-insights">
            {result.strengths?.length > 0 && (
              <article className="adp2-analysis-card is-positive">
                <h4><i className="fa-solid fa-circle-check" />{uiText(" Strengths")}</h4>
                {renderAnalysisList(result.strengths, 'No strengths were recorded.')}
              </article>
            )}
            {result.improvement_areas?.length > 0 && (
              <article className="adp2-analysis-card is-warning">
                <h4><i className="fa-solid fa-arrow-trend-up" />{uiText(" Areas for Improvement")}</h4>
                {renderAnalysisList(result.improvement_areas, 'No improvement areas were recorded.')}
              </article>
            )}
          </div>
        )}

        {selectedTranscript.length > 0 && (
          <details className="adp2-assessment-transcript">
            <summary>{uiText("Interview transcript")}</summary>
            <div>
              {selectedTranscript.map((turn, index) => (
                <p key={turn.id || index}>
                  <strong>{turn.role === 'candidate' ? uiText("Candidate") : uiText("AI interviewer")}:</strong> {turn.text}
                </p>
              ))}
            </div>
          </details>
        )}

        {questions.length > 0 && (
          <div className="adp2-question-review">
            <h3>{uiText("Response Review")}</h3>
            {questions.map((question, index) => {
              const questionId = firstValue(question.id, question.questionId, String(index));
              const questionText = firstValue(question.text, question.question, question.title, `Question ${index + 1}`);
              const questionEvaluation = assessmentQuestionEvaluation(result.question_breakdown, {
                ...question,
                id: questionId,
                text: questionText
              });
              const answer = assessmentAnswerText(
                firstValue(questionEvaluation?.answer, response.answers?.[questionId]),
                'No answer submitted.'
              );
              const evaluationText = displayText(firstValue(
                questionEvaluation?.evaluation,
                questionEvaluation?.analysis,
                questionEvaluation?.feedback,
                questionEvaluation?.summary,
                questionEvaluation?.reasoning,
                ''
              ));

              return (
                <article key={questionId}>
                  <h4>{index + 1}. {questionText}</h4>
                  <div className="adp2-question-answer">
                    <span>{uiText("Candidate answer")}</span>
                    <MarkdownBody>{answer}</MarkdownBody>
                  </div>
                  {(evaluationText || questionEvaluation?.score !== undefined) && (
                    <div className="adp2-question-evaluation">
                      <span>{uiText("Evaluation")}{questionEvaluation?.score !== undefined ? ` · ${questionEvaluation.score}` : ''}</span>
                      {evaluationText && <MarkdownBody>{evaluationText}</MarkdownBody>}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderInterviewAnalysis = (step) => {
    const stepInterviews = interviewsForStep(step);
    return (
      <div className="adp2-journey-block">
        <div className="adp2-section-heading-row">
          <h3>{uiText("Interview Analysis")}</h3>
          <button type="button" className="adp2-text-action" onClick={() => onScheduleInterview(step)}>{uiText("Schedule interview")}</button>
        </div>
        {loadingInterviews ? <EmptyBlock>{uiText("Loading interviews…")}</EmptyBlock> : stepInterviews.length ? (
          <div className="adp2-interview-list">
            {stepInterviews.map((interview) => {
              const analysis = displayText(firstValue(interview.analysis, interview.summary, interview.feedback, interview.evaluation));
              return (
                <article key={interview.id}>
                  <div>
                    <strong>{firstValue(interview.title, step.title, 'Interview')}</strong>
                    <p>{[interview.date, interview.time, interview.interviewer].filter(Boolean).join(' · ')}</p>
                    {interview.notes && <MarkdownBody>{interview.notes}</MarkdownBody>}
                    {analysis && <div className="adp2-interview-analysis-copy"><MarkdownBody>{analysis}</MarkdownBody></div>}
                  </div>
                  <div className="adp2-inline-actions">
                    <button type="button" onClick={() => onRescheduleInterview(interview, step)}>{uiText("Reschedule")}</button>
                    {interview.status !== 'cancelled' && <button type="button" className="is-danger" onClick={() => onCancelInterview(interview)}>{uiText("Cancel")}</button>}
                  </div>
                </article>
              );
            })}
          </div>
        ) : <EmptyBlock>{uiText("No interview has been scheduled for this step.")}</EmptyBlock>}
      </div>
    );
  };

  const renderJourney = () => {
    const isInitialAnalysis = selectedStep?.id === 'initial-analysis';
    const { event: persistedEvaluationEvent, relatedAssessment } = evaluationContextForStep(selectedStep);
    // Bind by persisted workflow/source identity. Selecting the globally newest
    // event would put results under the wrong step when departments differ.
    const stepEvaluationState = firstPopulatedValue(
      relatedAssessment?.liveReview?.evaluationState,
      relatedAssessment?.evaluationState,
      {}
    ) || {};
    const transientStatus = String(stepEvaluationState.status || '').toLowerCase().replace(/-/g, '_');
    const hasTransientState = [
      'queued',
      'pending',
      'evaluating',
      'processing',
      'in_progress',
      'technical_failure',
      'evaluation_failed',
      'failed',
    ].includes(transientStatus);
    // Queued work is not part of the immutable ledger yet. Synthesize a
    // display-only state from this selected assessment record; never add it to
    // the event collection where it could bind to another workflow step.
    const selectedEvaluationEvent = persistedEvaluationEvent || (
      relatedAssessment?.response && hasTransientState
        ? {
          eventId: `pending-${selectedStep.stageId}-${selectedStep.id}`,
          sourceType: relatedAssessment?.assessment?.deliveryType === 'two-way-voice'
            ? 'two_way_ai_interview'
            : String(selectedStep?.type || '').toLowerCase() === 'ai-interview'
              ? 'one_way_ai_interview'
              : 'assessment',
          stageId: selectedStep.stageId,
          substageId: selectedStep.id,
          status: transientStatus,
        }
        : null
    );
    const selectedStepSubtitle = timelineStepSubtitle(selectedStep, selectedJourneyIndex);

    return (
      <section className="adp2-tab-panel adp2-journey" aria-labelledby="adp2-tab-journey">
        <aside className="adp2-timeline-panel">
          <p className="adp2-eyebrow">{uiText("Timeline")}</p>
          <div className="adp2-timeline">
            {visibleJourneySteps.map((step, index) => {
              // Selection controls the visual emphasis independently from the
              // candidate's real workflow position. Move the gradient with
              // the selected analysis while aria-current still identifies the
              // actual current hiring step for assistive technology.
              const connectorClass = [
                index + 1 === selectedJourneyIndex ? 'connects-to-selected' : '',
                index === selectedJourneyIndex && index < visibleJourneySteps.length - 1 ? 'connects-from-selected' : '',
              ].filter(Boolean).join(' ');
              const className = `adp2-timeline-step ${index === currentIndex ? 'is-current' : 'is-complete'} ${index === selectedJourneyIndex ? 'is-selected' : ''} ${connectorClass}`;
              const subtitle = timelineStepSubtitle(step, index);
              const content = (
                <>
                  <span className="adp2-timeline-marker" />
                  <span>
                    <strong>{step.title}</strong>
                    {subtitle && <small>{subtitle}</small>}
                  </span>
                </>
              );

              return (
                <button
                  type="button"
                  key={`${step.stageId}-${step.id}`}
                  className={className}
                  aria-current={index === currentIndex ? 'step' : undefined}
                  aria-pressed={index === selectedJourneyIndex}
                  onClick={() => setSelectedJourneyIndex(index)}
                >
                  {content}
                </button>
              );
            })}
          </div>
        </aside>

        <div className="adp2-journey-content">
          <div className="adp2-journey-title-row">
            <div>
              {selectedStepSubtitle && <p className="adp2-eyebrow">{selectedStepSubtitle}</p>}
              <h2>{selectedStep?.title}</h2>
            </div>
          </div>

          {renderEvaluationJourneyEvent(selectedEvaluationEvent, { allowLegacyScore: isInitialAnalysis })}

          {isInitialAnalysis && renderInitialAnalysis(selectedEvaluationEvent)}

          {!isInitialAnalysis && selectedStep?.description && (
            <article className="adp2-journey-copy">
              <h3>{uiText("Summary")}</h3>
              <MarkdownBody>{selectedStep.description}</MarkdownBody>
            </article>
          )}

          {isInterviewStep(selectedStep) && renderInterviewAnalysis(selectedStep)}

          {isAssessmentStep(selectedStep) && (
            <div className="adp2-journey-block">
              <h3>{uiText("Assessment Result")}</h3>
              {renderAssessmentAnalysis(selectedStep, selectedEvaluationEvent)}
            </div>
          )}

          {!isInitialAnalysis && buddyInfo && (
            <div className="adp2-journey-block">
              <h3>{uiText("Assigned Buddy")}</h3>
              <p>{[buddyInfo.name, buddyInfo.position, buddyInfo.email].filter(Boolean).join(' · ')}</p>
            </div>
          )}

          {!isInitialAnalysis && firstWeekChecklist?.checklist && (
            <div className="adp2-journey-block">
              <h3>{uiText("First Week Checklist")}</h3>
              {Object.entries(firstWeekChecklist.checklist).map(([day, tasks]) => (
                <div className="adp2-checklist-day" key={day}>
                  <h4>{humanize(day)}</h4>
                  {(tasks || []).map((task, index) => (
                    <label key={`${day}-${index}`}>
                      <input type="checkbox" checked={Boolean(checklistProgress?.[day]?.[index])} onChange={() => onToggleChecklist(day, index)} />
                      <span>{task}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  };

  const updateMentionContext = (target, value, cursorPosition) => {
    const textBeforeCursor = value.slice(0, cursorPosition);
    const match = textBeforeCursor.match(/(?:^|\s)@([^@\s]*)$/);
    if (!match) {
      setMentionContext(null);
      return;
    }
    setMentionContext({
      target,
      query: match[1].toLowerCase(),
      start: cursorPosition - match[1].length - 1,
      end: cursorPosition,
    });
  };

  const selectMention = (member) => {
    if (!mentionContext || !member) return;
    const isReply = mentionContext.target === 'reply';
    const value = isReply ? replyContent : newNoteContent;
    const mentionLabel = `@${firstValue(member.name, member.email?.split('@')[0], 'Team member')}`;
    const updated = `${value.slice(0, mentionContext.start)}${mentionLabel} ${value.slice(mentionContext.end)}`;
    const mention = { uid: member.uid, email: member.email || '', name: member.name || '' };
    if (isReply) {
      setReplyContent(updated);
      setReplyMentions((previous) => previous.some((entry) => entry.uid === member.uid) ? previous : [...previous, mention]);
    } else {
      setNewNoteContent(updated);
      setNoteMentions((previous) => previous.some((entry) => entry.uid === member.uid) ? previous : [...previous, mention]);
    }
    setMentionContext(null);
  };

  const mentionMatches = teamMembers.filter((member) => {
    if (!mentionContext) return false;
    const searchable = `${member.name || ''} ${member.email || ''}`.toLowerCase();
    return searchable.includes(mentionContext.query);
  }).slice(0, 6);

  const submitTeamNote = async () => {
    if (!newNoteContent.trim() || noteSubmitting) return;
    setNoteSubmitting(true);
    try {
      const saved = await onAddNote({
        content: newNoteContent.trim(),
        priority: notePriority,
        mentions: noteMentions,
      });
      if (saved) {
        setNotePriority('normal');
        setNoteMentions([]);
        setMentionContext(null);
      }
    } finally {
      setNoteSubmitting(false);
    }
  };

  const submitReply = async (threadId) => {
    if (!replyContent.trim() || replySubmitting) return;
    setReplySubmitting(true);
    try {
      const saved = await onAddNote({
        content: replyContent.trim(),
        parentId: threadId,
        threadId,
        priority: 'normal',
        mentions: replyMentions,
      });
      if (saved) {
        setReplyContent('');
        setReplyMentions([]);
        setReplyingToNoteId('');
      }
    } finally {
      setReplySubmitting(false);
    }
  };

  const renderNoteAuthor = (note) => {
    const noteUser = firstValue(note.user, note.sender, 'Team member');
    const member = getMember(note.uid, noteUser);
    const displayName = firstValue(member.name, noteUser, 'Team member');
    return { member, displayName };
  };

  const renderTeam = () => (
    <section className="adp2-tab-panel adp2-team" aria-labelledby="adp2-tab-team">
      <div className="adp2-notes-section">
        <div className="adp2-team-heading">
          <div>
            <h2>{uiText("Team notes")}</h2>
          </div>
          <span className="adp2-thread-count">{noteThreads.length} {noteThreads.length === 1 ? uiText("thread") : uiText("threads")}</span>
        </div>

        <div className="adp2-note-composer">
          <div className="adp2-composer-heading">
            <span className="adp2-composer-icon"><i className="fa-regular fa-note-sticky" aria-hidden="true" /></span>
            <div><strong>{uiText("Add a team note")}</strong></div>
          </div>
          <textarea
            ref={teamMessageBoxRef}
            value={newNoteContent}
            onChange={(event) => {
              setNewNoteContent(event.target.value);
              updateMentionContext('note', event.target.value, event.target.selectionStart);
            }}
            placeholder={uiText("Share useful context, a concern, or a follow-up…")}
            rows={3}
          />
          {mentionContext?.target === 'note' && (
            <div className="adp2-mention-menu" role="listbox" aria-label={uiText("Mention a colleague")}>
              {mentionMatches.length ? mentionMatches.map((member) => (
                <button type="button" key={member.uid} onMouseDown={(event) => event.preventDefault()} onClick={() => selectMention(member)}>
                  <span className="adp2-avatar">{member.photoUrl ? <img src={member.photoUrl} alt="" /> : String(member.name || member.email || '?').slice(0, 1).toUpperCase()}</span>
                  <span><strong>{member.name || uiText("Team member")}</strong><small>{member.email}</small></span>
                </button>
              )) : <p>{uiText("No matching colleagues")}</p>}
            </div>
          )}
          <div className="adp2-note-controls">
            <div className="adp2-priority-control" role="group" aria-label={uiText("Note priority")}>
              {NOTE_PRIORITIES.map((priority) => (
                <button
                  type="button"
                  key={priority.value}
                  className={notePriority === priority.value ? 'is-selected' : ''}
                  onClick={() => setNotePriority(priority.value)}
                >
                  <i className={`fa-solid ${priority.value === 'urgent' ? 'fa-circle-exclamation' : priority.value === 'important' ? 'fa-flag' : 'fa-minus'}`} aria-hidden="true" />
                  {uiText(priority.label)}
                </button>
              ))}
            </div>
          </div>
          <div className="adp2-composer-footer">
            <span>{uiText("Type @ to mention a colleague")}</span>
            <button className="adp2-note-submit" type="button" onClick={submitTeamNote} disabled={!newNoteContent.trim() || noteSubmitting}>
              {noteSubmitting ? uiText("Posting…") : uiText("Post note")} <i className="fa-solid fa-plus" aria-hidden="true" />
            </button>
          </div>
        </div>

        {noteThreads.length ? (
          <div className="adp2-note-threads">
            {noteThreads.map(({ root, replies }) => {
              const { member, displayName } = renderNoteAuthor(root);
              return (
                <article className={`adp2-note-thread adp2-priority-${root.priority}`} key={root.id}>
                  <div className="adp2-note-main">
                    <header className="adp2-note-header">
                      <span className="adp2-avatar">{member.photoUrl ? <img src={member.photoUrl} alt="" /> : String(displayName).slice(0, 1).toUpperCase()}</span>
                      <div className="adp2-note-author"><strong>{displayName}</strong><time>{root.timestamp ? formatDateTime(root.timestamp) : uiText("Recently")}</time></div>
                      {root.priority !== 'normal' && <span className={`adp2-priority-badge ${root.priority}`}><i className={`fa-solid ${root.priority === 'urgent' ? 'fa-circle-exclamation' : 'fa-flag'}`} aria-hidden="true" /> {root.priority}</span>}
                    </header>
                    <MarkdownBody mentions={root.mentions}>{firstValue(root.content, root.text, '')}</MarkdownBody>
                    <button
                      type="button"
                      className="adp2-reply-action"
                      onClick={() => {
                        setReplyingToNoteId(replyingToNoteId === root.id ? '' : root.id);
                        setReplyContent('');
                      }}
                    >
                      <i className="fa-regular fa-comment" aria-hidden="true" />{uiText(" Reply")}{replies.length ? ` · ${replies.length}` : ''}
                    </button>
                  </div>

                  {(replies.length > 0 || replyingToNoteId === root.id) && (
                    <div className="adp2-reply-thread">
                      {replies.map((reply) => {
                        const replyAuthor = renderNoteAuthor(reply);
                        return (
                          <div className="adp2-reply" key={reply.id}>
                            <span className="adp2-avatar">{replyAuthor.member.photoUrl ? <img src={replyAuthor.member.photoUrl} alt="" /> : String(replyAuthor.displayName).slice(0, 1).toUpperCase()}</span>
                            <div><header><strong>{replyAuthor.displayName}</strong>{reply.timestamp && <time>{formatDateTime(reply.timestamp)}</time>}</header><MarkdownBody mentions={reply.mentions}>{firstValue(reply.content, reply.text, '')}</MarkdownBody></div>
                          </div>
                        );
                      })}
                      {replyingToNoteId === root.id && (
                        <div className="adp2-reply-composer">
                          <textarea value={replyContent} onChange={(event) => { setReplyContent(event.target.value); updateMentionContext('reply', event.target.value, event.target.selectionStart); }} placeholder={uiText("Write a reply… Type @ to mention someone")} rows={2} autoFocus />
                          {mentionContext?.target === 'reply' && (
                            <div className="adp2-mention-menu" role="listbox" aria-label={uiText("Mention a colleague")}>
                              {mentionMatches.length ? mentionMatches.map((member) => (
                                <button type="button" key={member.uid} onMouseDown={(event) => event.preventDefault()} onClick={() => selectMention(member)}>
                                  <span className="adp2-avatar">{String(member.name || member.email || '?').slice(0, 1).toUpperCase()}</span>
                                  <span><strong>{member.name || uiText("Team member")}</strong><small>{member.email}</small></span>
                                </button>
                              )) : <p>{uiText("No matching colleagues")}</p>}
                            </div>
                          )}
                          <div><button type="button" onClick={() => { setReplyingToNoteId(''); setReplyContent(''); }}>{uiText("Cancel")}</button><button type="button" className="adp2-reply-submit" onClick={() => submitReply(root.id)} disabled={!replyContent.trim() || replySubmitting}>{replySubmitting ? uiText("Posting…") : uiText("Reply")}</button></div>
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        ) : <EmptyBlock>{uiText("No team notes yet. Add the first note to start the discussion.")}</EmptyBlock>}
      </div>
    </section>
  );

  const renderMessages = () => {
    const isImported = applicantData?.applicantType === 'imported';
    return (
      <section className="adp2-tab-panel adp2-messages" aria-labelledby="adp2-tab-messages">
        {isImported ? (
          <div className="adp2-message-empty">
            <i className="fa-regular fa-envelope" />
            <h3>{uiText("Platform messaging is unavailable for imported candidates.")}</h3>
            {applicantEmail && applicantEmail !== 'No Email On File' && (
              <a href={`mailto:${applicantEmail}`}>{uiText("Email ")}{firstName}</a>
            )}
          </div>
        ) : (
          <>
            <div className="adp2-message-list" ref={messagesRef} aria-live="polite">
              {messages?.length ? messages.map((message, index) => {
                const outgoing = message.user === userUID || message.sender === 'employer' || message.sender === 'company';
                const body = firstValue(message.message, message.text, '');
                const currentDateKey = messageDateKey(message.timestamp);
                const previousDateKey = index > 0 ? messageDateKey(messages[index - 1]?.timestamp) : '';
                const startsNewDate = currentDateKey && currentDateKey !== previousDateKey;
                return (
                  <React.Fragment key={message.id || index}>
                    {startsNewDate && (
                      <div className="adp2-message-date-separator" role="separator" aria-label={formatMessageDateLabel(message.timestamp)}>
                        <span>{formatMessageDateLabel(message.timestamp)}</span>
                      </div>
                    )}
                    <article className={outgoing ? 'is-outgoing' : 'is-incoming'}>
                      <MarkdownBody>{body}</MarkdownBody>
                      {message.timestamp && (
                        <time title={formatDateTime(message.timestamp)}>{formatMessageTimestamp(message.timestamp)}</time>
                      )}
                    </article>
                  </React.Fragment>
                );
              }) : <EmptyBlock>{uiText("No messages yet. Start the conversation below.")}</EmptyBlock>}
            </div>
            <div className="adp2-message-composer">
              <textarea
                ref={messageBoxRef}
                value={newMessage}
                onChange={(event) => setNewMessage(event.target.value)}
                onKeyDown={onMessageKeyDown}
                placeholder={`Write a message to ${firstName}…`}
                rows={1}
              />
              <button type="button" onClick={onSendMessage} disabled={!newMessage.trim()} aria-label={`Send message to ${firstName}`}>
                <span>{uiText("Send")}</span><i className="fa-solid fa-paper-plane" aria-hidden="true" />
              </button>
            </div>
          </>
        )}
      </section>
    );
  };

  if (isLoading) {
    return (
      <div className="adp2-overlay" role="presentation" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="adp2-modal adp2-modal--loading" role="dialog" aria-modal="true" aria-label={uiText("Loading applicant details")}>
          <button type="button" className="adp2-close" onClick={onClose} aria-label={uiText("Close applicant details")}>
            <i className="fa-solid fa-xmark" />
          </button>
          <div className="adp2-loading-state">
            <AppLoading inline label={uiText("Loading candidate details...")} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="adp2-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="adp2-modal" role="dialog" aria-modal="true" aria-labelledby="adp2-candidate-name">
        <button type="button" className="adp2-close" onClick={onClose} aria-label={uiText("Close applicant details")}>
          <i className="fa-solid fa-xmark" />
        </button>

        <header className="adp2-header">
          <div className="adp2-candidate-identity">
            <p className="adp2-eyebrow">{uiText("Candidate Information")}</p>
            <div className="adp2-name-row">
              <h1 id="adp2-candidate-name">{candidateName}</h1>
              <div className="adp2-contact-actions">
                {applicantEmail && applicantEmail !== 'No Email On File' && (
                  <button type="button" onClick={onCopyEmail} title={applicantEmail} aria-label={`Copy ${applicantEmail}`}>
                    <i className="fa-solid fa-envelope" />
                  </button>
                )}
                {phone && (
                  <a href={`tel:${phone}`} title={phone} aria-label={`Call ${phone}`}><i className="fa-solid fa-phone" /></a>
                )}
                {linkedin && (
                  <a href={linkedin} target="_blank" rel="noreferrer" title={uiText("LinkedIn")} aria-label={uiText("Open LinkedIn profile")}>
                    <i className="fa-brands fa-linkedin" />
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="adp2-score-summary">
            <p className="adp2-eyebrow">{uiText("Fit Score")}</p>
            <strong>{resolvedFitScore !== null ? `${resolvedFitScore}%` : '—'}</strong>
            {scoreDelta !== null && scoreDelta !== 0 && <span>({scoreDelta > 0 ? '+' : ''}{scoreDelta})</span>}
          </div>

          <div className="adp2-actions" ref={actionsRef}>
            <button
              type="button"
              className="adp2-actions-pill"
              onClick={() => setActionsOpen((open) => !open)}
              aria-expanded={actionsOpen}
              aria-haspopup="menu"
            >
              <i className={`fa-solid ${actionsOpen ? 'fa-circle-chevron-up' : 'fa-circle-chevron-down'}`} aria-hidden="true" />
              <span>{uiText("Actions")}</span>
            </button>
            {actionsOpen && (
              <div className="adp2-actions-menu">
                <button type="button" onClick={() => invokeAction(onToggleStarred)} disabled={togglingStarred}>
                  <i className={`${isStarred ? 'fa-solid' : 'fa-regular'} fa-star`} /> {isStarred ? uiText("Unstar applicant") : uiText("Star applicant")}
                </button>
                <button type="button" onClick={() => invokeAction(onOpenResume)} disabled={!resumeLink}>
                  <i className="fa-regular fa-file-lines" />{uiText(" View resume")}</button>
                <button type="button" onClick={() => invokeAction(onAnalyze)} disabled={isAnalyzing || !resumeLink}>
                  <i className="fa-solid fa-wand-magic-sparkles" /> {isAnalyzing ? uiText("Analyzing…") : uiText("Analyze CV")}
                </button>
                <button type="button" onClick={() => invokeAction(onScheduleInterview)}>
                  <i className="fa-regular fa-calendar-plus" />{uiText(" Schedule interview")}</button>
                {offerApproval && (
                  <button type="button" onClick={() => invokeAction(() => onViewApprovalDetails?.(offerApproval))}>
                    <i className="fa-regular fa-envelope-open" />{uiText(" Review pending offer")}</button>
                )}
                {applicantData?.applicantType === 'imported' && (
                  <button type="button" className="is-danger" onClick={() => invokeAction(onRemove)}>
                    <i className="fa-regular fa-trash-can" />{uiText(" Remove from job")}</button>
                )}
                <button type="button" className="is-danger" onClick={() => invokeAction(onReject)}>
                  <i className="fa-regular fa-circle-xmark" />{uiText(" Reject candidate")}</button>
              </div>
            )}
          </div>
        </header>

        {offerApproval && (
          <button type="button" className="adp2-offer-alert" onClick={() => onViewApprovalDetails?.(offerApproval)}>
            <i className="fa-regular fa-envelope-open" />{uiText(" Offer awaiting approval — review offer")}</button>
        )}

        <nav className="adp2-tabs" aria-label={uiText("Applicant detail sections")} role="tablist">
          {tabs.map((tab) => (
            <button
              type="button"
              id={`adp2-tab-${tab.id}`}
              key={tab.id}
              className={activeTab === tab.id ? 'is-active' : ''}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {uiText(tab.label)}
            </button>
          ))}
        </nav>

        <main className={`adp2-content ${activeTab === 'messages' ? 'adp2-content--messages' : ''}`}>
          {activeTab === 'cv' && renderCv()}
          {activeTab === 'journey' && renderJourney()}
          {activeTab === 'team' && renderTeam()}
          {activeTab === 'messages' && renderMessages()}
        </main>
      </div>
    </div>
  );
};

export { flattenWorkflow, resolveCvSections };
export default ApplicantDetailsPopupV2;
